import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time exceptions so a single bad value (e.g. an unexpected API
 * shape) shows a readable error instead of unmounting the tree to a blank page.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced in the console for debugging; the UI below shows the message.
    console.error('Render error:', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-lg border border-red-300 bg-red-50 p-5">
          <h1 className="text-lg font-semibold text-red-800">Something went wrong</h1>
          <p className="mt-1 text-sm text-red-700">
            The page hit an unexpected error and couldn’t render. Try again, and if it keeps
            happening, share the detail below.
          </p>
          <pre className="mt-3 max-h-60 overflow-auto whitespace-pre-wrap rounded bg-white/70 p-3 text-xs text-red-900">
            {error.message}
          </pre>
          <button
            onClick={this.reset}
            className="mt-4 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
